import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { EASE, LineReveal, Reveal, Stagger, StaggerItem, motion, useReducedMotion } from "../lib/motion";

export const Container = ({ className = "", children, ...rest }) => (
  <div className={`mx-auto w-full max-w-[1320px] px-5 sm:px-8 lg:px-12 ${className}`} {...rest}>
    {children}
  </div>
);

/** Every section eases in once as the reader reaches it. Pass `flat` to opt out. */
export const Section = ({ className = "", children, flat = false, ...rest }) => {
  const reduce = useReducedMotion();
  const cls = `py-20 sm:py-28 lg:py-32 ${className}`;
  if (flat || reduce) {
    return <section className={cls} {...rest}>{children}</section>;
  }
  return (
    <motion.section
      className={cls}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.01, margin: "600px 0px -40px 0px" }}
      transition={{ duration: 0.7, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.section>
  );
};

export const Eyebrow = ({ children, className = "" }) => (
  <p className={`eyebrow ${className}`}>{children}</p>
);

export const H1 = ({ children, className = "" }) => (
  <h1 className={`h1-page ${className}`}>{children}</h1>
);

export const H2 = ({ children, className = "" }) => (
  <h2 className={`h2-section ${className}`}>{children}</h2>
);

export const H3 = ({ children, className = "" }) => (
  <h3 className={`h3-card ${className}`}>{children}</h3>
);

export const Lede = ({ children, className = "" }) => (
  <p className={`measure body-lg text-dim ${className}`}>{children}</p>
);

export const Prose = ({ children, className = "" }) => (
  <div className={`measure space-y-5 body ${className}`}>{children}</div>
);

/**
 * Editorial page opener. The headline rises from behind a clip, the lede follows,
 * an optional `aside` card sits beside it on large screens.
 */
export function PageHero({
  eyebrow, title, lines, lede, meta, aside, children, className = "", bordered = true,
}) {
  return (
    <section
      data-testid="page-hero"
      className={`${bordered ? "border-b border-rule" : ""} pb-14 pt-10 sm:pb-16 sm:pt-12 lg:pb-20 lg:pt-14 ${className}`}
    >
      <Container className={aside ? "grid items-start gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16" : ""}>
        <div>
          {eyebrow && (
            <motion.p
              className="eyebrow"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {eyebrow}
            </motion.p>
          )}
          <H1 className="mt-5 max-w-4xl">
            {lines ? <LineReveal lines={lines} delay={0.08} /> : title}
          </H1>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.34, ease: EASE }}
          >
            {lede && <Lede className="mt-6">{lede}</Lede>}
            {meta}
            {children}
          </motion.div>
        </div>
        {aside && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.46, ease: EASE }}
            className="lg:pt-4"
          >
            {aside}
          </motion.div>
        )}
      </Container>
    </section>
  );
}

/** Wide-metric section header: 5px-tracked eyebrow, clamped serif title, 50px gap to the grid. */
export function SectionHeader({ eyebrow, title, lede, action, center = false, className = "" }) {
  return (
    <Reveal className={`${center ? "text-center" : ""} ${className}`} style={{ marginBottom: 50 }}>
      <div className={`flex flex-wrap items-end gap-6 ${center ? "justify-center" : "justify-between"}`}>
        <div className={center ? "mx-auto" : ""}>
          {eyebrow && <p className="eyebrow-wide">{eyebrow}</p>}
          <h2 className={`section-title mt-5 max-w-3xl ${center ? "mx-auto" : ""}`}>{title}</h2>
        </div>
        {action}
      </div>
      {lede && (
        <p className={`ui mt-5 max-w-2xl text-base leading-[1.7] text-dim ${center ? "mx-auto" : ""}`}>
          {lede}
        </p>
      )}
    </Reveal>
  );
}

/** Eyebrow + H2 + optional lede, revealed together on scroll. */
export function SectionHead({ eyebrow, title, lede, action, className = "" }) {
  return (
    <Reveal className={`mb-12 flex flex-wrap items-end justify-between gap-6 lg:mb-16 ${className}`}>
      <div className="max-w-3xl">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        {title && <H2 className="mt-4">{title}</H2>}
        {lede && <Lede className="mt-5">{lede}</Lede>}
      </div>
      {action}
    </Reveal>
  );
}

export function Breadcrumbs({ trail, testId = "breadcrumbs" }) {
  return (
    <nav aria-label="Breadcrumb" data-testid={testId} className="ui flex flex-wrap items-center gap-1 text-xs text-dim">
      {trail.map((t, i) => (
        <span key={`${t.label}-${i}`} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3 opacity-50" aria-hidden="true" />}
          {t.to && i < trail.length - 1 ? (
            <Link to={t.to} className="transition-colors hover:text-gold">{t.label}</Link>
          ) : (
            <span aria-current="page" className="text-ink">{t.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function ButtonLink({ to, href, variant = "primary", className = "", children, ...rest }) {
  const cls = `btn btn-${variant} ${className}`;
  if (href) {
    return <a href={href} className={cls} {...rest}>{children}</a>;
  }
  return <Link to={to} className={cls} {...rest}>{children}</Link>;
}

export function Field({ label, hint, error, children, htmlFor }) {
  return (
    <div className="w-full">
      <label className="field-label" htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && <p className="ui mt-1 text-xs text-dim">{hint}</p>}
      {error && <p className="ui mt-1 text-xs text-alert">{error}</p>}
    </div>
  );
}

export function ConsentCheckbox({ id, checked, onChange, children, testId }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 text-sm">
      <input
        id={id}
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 shrink-0 accent-[var(--c-gold)]"
      />
      <span className="text-dim">{children}</span>
    </label>
  );
}

export function Spinner({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-3 py-16 text-dim ui text-sm" role="status">
      <span className="h-3 w-3 animate-pulse rounded-full bg-gold" />
      {label}…
    </div>
  );
}

export function EmptyState({ title, children, testId = "empty-state" }) {
  return (
    <div data-testid={testId} className="card-s elev p-10 text-center">
      <H3 className="mb-2">{title}</H3>
      <p className="ui text-sm text-dim">{children}</p>
    </div>
  );
}

/** Thin numbered rule used to open editorial blocks. */
export function Ordinal({ n, className = "" }) {
  return (
    <span className={`num block text-3xl leading-none text-gold/75 sm:text-4xl ${className}`}>
      {String(n).padStart(2, "0")}
    </span>
  );
}

export { Reveal, Stagger, StaggerItem };
